import { DRIVING_TRACK_CENTER_RADIUS } from '../model/stageGround';
import {
  createCircularRoute,
  createFigureEightRoute,
  headingBetween,
  sampleCatmullRomRoute,
  type CourseObstacle,
  type CoursePoint,
  type CourseRoute,
  type CourseSpawn,
} from '../model/drivingCourse';

export type DrivingCourseKind = 'circuit' | 'sprint';

export interface CourseCheckpoint {
  id: string;
  label: string;
  x: number;
  z: number;
  radius: number;
  /** 자기교차 중심선에서 어느 통과 방향인지 구분하는 canonical route sample index. */
  routeIndex?: number;
}

export interface CourseMapBounds {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}

export interface DrivingCourseOption {
  id: string;
  name: string;
  shortName: string;
  description: string;
  icon: string;
  kind: DrivingCourseKind;
  spawn: CourseSpawn;
  checkpoints: readonly CourseCheckpoint[];
  mapBounds: CourseMapBounds;
  /** 렌더·노면 물리·미니맵이 공유하는 실제 중심선입니다. */
  route: CourseRoute;
}

export const COURSE_IDS = {
  SPEED_RING: 'speed-ring',
  ELEVATION_RUN: 'elevation-run',
  WINDING_RUN: 'winding-run',
  SLALOM: 'slalom',
  FIGURE_EIGHT: 'figure-eight',
} as const;

const RING = DRIVING_TRACK_CENTER_RADIUS;
const RING_ROUTE: CourseRoute = {
  points: createCircularRoute(0, 0, RING, RING, 256, true),
  closed: true,
  roadHalfWidth: 12,
  runoffWidth: 36,
};

const ELEVATION_POINTS: readonly CoursePoint[] = [
  { x: 92, z: 0 },
  { x: 120, z: 0 },
  { x: 560, z: 0 },
];
const ELEVATION_ROUTE: CourseRoute = {
  points: ELEVATION_POINTS,
  closed: false,
  roadHalfWidth: 13,
  runoffWidth: 24,
};

const WINDING_CONTROLS: readonly CoursePoint[] = [
  { x: -940, z: -420 },
  { x: -800, z: -420 },
  { x: -690, z: -300 },
  { x: -560, z: -470 },
  { x: -410, z: -270 },
  { x: -255, z: -440 },
  { x: -100, z: -300 },
];
const WINDING_ROUTE: CourseRoute = {
  points: sampleCatmullRomRoute(WINDING_CONTROLS, false, 18),
  closed: false,
  roadHalfWidth: 10,
  runoffWidth: 24,
};

const SLALOM_CONTROLS: readonly CoursePoint[] = [
  { x: -720, z: 250 },
  { x: -706, z: 360 },
  { x: -734, z: 470 },
  { x: -706, z: 580 },
  { x: -734, z: 690 },
  { x: -706, z: 800 },
  { x: -720, z: 910 },
];
const SLALOM_CONE_COLLIDER = Object.freeze({
  radius: 0.44,
  restitution: 0.08,
  tangentRetention: 0.58,
});
const SLALOM_CONES: readonly CourseObstacle[] = SLALOM_CONTROLS.slice(1, -1).map(
  (point, index) => ({
    id: `slalom-cone-${index + 1}`,
    type: 'cone',
    x: -720,
    z: point.z,
    height: 0.84,
    collider: SLALOM_CONE_COLLIDER,
  })
);
const SLALOM_ROUTE: CourseRoute = {
  points: sampleCatmullRomRoute(SLALOM_CONTROLS, false, 16),
  closed: false,
  roadHalfWidth: 14,
  runoffWidth: 18,
  obstacles: SLALOM_CONES,
};

const FIGURE_EIGHT_CENTER = { x: 420, z: 470 };
const FIGURE_EIGHT_RADIUS_X = 280;
const FIGURE_EIGHT_RADIUS_Z = 170;
const FIGURE_EIGHT_ROUTE: CourseRoute = {
  points: createFigureEightRoute(
    FIGURE_EIGHT_CENTER.x,
    FIGURE_EIGHT_CENTER.z,
    FIGURE_EIGHT_RADIUS_X,
    FIGURE_EIGHT_RADIUS_Z
  ),
  closed: true,
  roadHalfWidth: 11.5,
  runoffWidth: 22,
};

function figureEightPoint(angle: number): CoursePoint {
  return {
    x: FIGURE_EIGHT_CENTER.x + Math.sin(angle) * FIGURE_EIGHT_RADIUS_X,
    z: FIGURE_EIGHT_CENTER.z + Math.sin(angle * 2) * FIGURE_EIGHT_RADIUS_Z,
  };
}

const figureEightSpawnPoint = figureEightPoint(-0.035);
const figureEightAheadPoint = figureEightPoint(0.035);

export const drivingCourseOptions: readonly DrivingCourseOption[] = [
  {
    id: COURSE_IDS.SPEED_RING,
    name: '고속 순환 트랙',
    shortName: '스피드 링',
    description: '외곽 링 · 4 체크포인트 랩',
    icon: '◯',
    kind: 'circuit',
    spawn: { x: RING, z: 4, heading: Math.PI / 2 },
    // 출발점부터 시계 방향(-Z)으로 통과하는 순서입니다.
    checkpoints: [
      { id: 'south', label: 'S1', x: 0, z: -RING, radius: 72 },
      { id: 'west', label: 'S2', x: -RING, z: 0, radius: 72 },
      { id: 'north', label: 'S3', x: 0, z: RING, radius: 72 },
      { id: 'finish', label: 'FIN', x: RING, z: 0, radius: 72 },
    ],
    mapBounds: { minX: -1240, maxX: 1240, minZ: -1240, maxZ: 1240 },
    route: RING_ROUTE,
  },
  {
    id: COURSE_IDS.ELEVATION_RUN,
    name: '고저차 스프린트',
    shortName: '힐 코스',
    description: '오르막·내리막 · 포인트 투 포인트',
    icon: '⌁',
    kind: 'sprint',
    spawn: { x: 92, z: 0, heading: 0 },
    checkpoints: [
      { id: 'climb', label: 'CP1', x: 220, z: 0, radius: 24 },
      { id: 'summit', label: 'CP2', x: 340, z: 0, radius: 24 },
      { id: 'descent', label: 'CP3', x: 470, z: 0, radius: 24 },
      { id: 'finish', label: 'FIN', x: 555, z: 0, radius: 24 },
    ],
    mapBounds: { minX: 70, maxX: 585, minZ: -84, maxZ: 84 },
    route: ELEVATION_ROUTE,
  },
  {
    id: COURSE_IDS.WINDING_RUN,
    name: '테크니컬 와인딩',
    shortName: '와인딩',
    description: '연속 S코너 · 포인트 투 포인트',
    icon: '〰',
    kind: 'sprint',
    spawn: {
      ...WINDING_CONTROLS[0],
      heading: headingBetween(WINDING_CONTROLS[0], WINDING_CONTROLS[1]),
    },
    checkpoints: WINDING_CONTROLS.slice(1).map((point, index) => ({
      id: index === WINDING_CONTROLS.length - 2 ? 'finish' : `winding-${index + 1}`,
      label: index === WINDING_CONTROLS.length - 2 ? 'FIN' : `W${index + 1}`,
      ...point,
      radius: 19,
    })),
    mapBounds: { minX: -980, maxX: -60, minZ: -520, maxZ: -220 },
    route: WINDING_ROUTE,
  },
  {
    id: COURSE_IDS.SLALOM,
    name: '콘 슬라럼',
    shortName: '슬라럼',
    description: '교대 콘 게이트 · 정밀 조향',
    icon: '⋮',
    kind: 'sprint',
    spawn: {
      ...SLALOM_CONTROLS[0],
      heading: headingBetween(SLALOM_CONTROLS[0], SLALOM_CONTROLS[1]),
    },
    checkpoints: SLALOM_CONTROLS.slice(1).map((point, index) => ({
      id: index === SLALOM_CONTROLS.length - 2 ? 'finish' : `slalom-${index + 1}`,
      label: index === SLALOM_CONTROLS.length - 2 ? 'FIN' : `S${index + 1}`,
      ...point,
      // 콘 중심선과 각 게이트 중심은 14m 떨어져 있습니다. 반경이 14m 이상이면 직선으로
      // 모든 게이트를 밟을 수 있으므로, 좌우 판정 원이 겹치지 않는 9m로 제한합니다.
      radius: 9,
    })),
    mapBounds: { minX: -800, maxX: -640, minZ: 205, maxZ: 955 },
    route: SLALOM_ROUTE,
  },
  {
    id: COURSE_IDS.FIGURE_EIGHT,
    name: '피겨 에이트',
    shortName: '8자 코스',
    description: '교차 코너 · 8 체크포인트 랩',
    icon: '∞',
    kind: 'circuit',
    spawn: {
      ...figureEightSpawnPoint,
      heading: headingBetween(figureEightSpawnPoint, figureEightAheadPoint),
    },
    // 교차점 양쪽의 ordered gate가 반대 가지를 지름길로 통과하는 것을 막습니다.
    checkpoints: [
      { id: 'right-high', label: 'E1', ...figureEightPoint(Math.PI / 4), radius: 22, routeIndex: 30 },
      { id: 'right-tip', label: 'E2', ...figureEightPoint(Math.PI / 2), radius: 22, routeIndex: 60 },
      { id: 'right-low', label: 'E3', ...figureEightPoint((Math.PI * 3) / 4), radius: 22, routeIndex: 90 },
      { id: 'cross', label: 'E4', ...figureEightPoint(Math.PI), radius: 18, routeIndex: 120 },
      { id: 'left-high', label: 'E5', ...figureEightPoint((Math.PI * 5) / 4), radius: 22, routeIndex: 150 },
      { id: 'left-tip', label: 'E6', ...figureEightPoint((Math.PI * 3) / 2), radius: 22, routeIndex: 180 },
      { id: 'left-low', label: 'E7', ...figureEightPoint((Math.PI * 7) / 4), radius: 22, routeIndex: 210 },
      { id: 'finish', label: 'FIN', ...figureEightPoint(0), radius: 18, routeIndex: 0 },
    ],
    mapBounds: { minX: 100, maxX: 740, minZ: 260, maxZ: 680 },
    route: FIGURE_EIGHT_ROUTE,
  },
];

export const DEFAULT_DRIVING_COURSE_ID = COURSE_IDS.SPEED_RING;

export function findDrivingCourseById(courseId: string): DrivingCourseOption | undefined {
  return drivingCourseOptions.find((course) => course.id === courseId);
}
